import type { ShellInjectionRef } from '../kernel-contract.js';

/**
 * CAOS-135 — the GLOBAL SEARCH box, in the topbar's centre slot.
 *
 * One term, every object: submitting asks the kernel's `search/global` verb, which searches every
 * object with searchable fields and has already narrowed each group by the caller's object, field
 * and record access. This surface only draws what came back — it never filters, ranks or decides
 * visibility itself, so there is no second picture of access to drift.
 *
 * Where a hit leads: a record opens inside an APP, at `/<app>/<tab>/<recordId>`, the same address a
 * record link uses everywhere else in the workspace. The destination is the first installed app
 * (navigation-entries, in their placed order) with a tab showing that object. A hit in an object no
 * installed app shows is named but not linked — a link that leads nowhere is worse than saying
 * plainly that the record exists.
 *
 * Snippets arrive as runs of plain text marked matched or not, and are drawn with `textContent` and
 * `<mark>`, so a field value holding markup is shown as the characters it is.
 */

/** A minimal response shape, so a test stub need not build a whole `Response`. */
export interface SearchFetchResponse {
  json(): Promise<unknown>;
}

/** The fetch seam: a same-origin GET against the host `/api/*` proxy. */
export type SearchFetch = (path: string) => Promise<SearchFetchResponse>;

const defaultSearchFetch: SearchFetch = (path) =>
  fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  }) as unknown as Promise<SearchFetchResponse>;

let searchFetch: SearchFetch = defaultSearchFetch;

/** Override the search fetch seam (tests); `null` restores the default. */
export function setSearchFetch(fn: SearchFetch | null): void {
  searchFetch = fn ?? defaultSearchFetch;
}

/** Where a record of one object opens. */
export interface SearchDestination {
  readonly app: string;
  readonly tab: string;
}

/** Object key → the first app tab showing it, from the installed apps in their placed order. */
export function searchDestinations(apps: readonly ShellInjectionRef[]): Map<string, SearchDestination> {
  const out = new Map<string, SearchDestination>();
  for (const app of apps) {
    for (const tab of app.tabs ?? []) {
      if (tab.object && !out.has(tab.object)) out.set(tab.object, { app: app.sourceKey, tab: tab.key });
    }
  }
  return out;
}

interface SnippetPart {
  text: string;
  match: boolean;
}

interface SearchHit {
  recordId: string;
  name: string | null;
  snippet: SnippetPart[];
}

interface SearchGroup {
  object: string;
  label: string;
  count: number;
  countCapped: boolean;
  hits: SearchHit[];
}

type SearchAnswer =
  | { kind: 'ok'; groups: SearchGroup[] }
  | { kind: 'narrow'; guidance: string }
  | { kind: 'error' };

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

/** Read the kernel envelope defensively: nothing on the wire is trusted to have the declared shape. */
function readAnswer(payload: unknown): SearchAnswer {
  if (!isRecord(payload) || payload.ok !== true || !isRecord(payload.data)) return { kind: 'error' };
  const data = payload.data;
  if (data.outcome === 'narrow') return { kind: 'narrow', guidance: str(data.guidance) ?? '' };
  if (data.outcome !== 'ok' || !Array.isArray(data.groups)) return { kind: 'error' };
  const groups: SearchGroup[] = [];
  for (const g of data.groups) {
    if (!isRecord(g) || !str(g.object) || !Array.isArray(g.hits)) continue;
    const hits: SearchHit[] = [];
    for (const h of g.hits) {
      if (!isRecord(h) || !str(h.recordId)) continue;
      const snippet = Array.isArray(h.snippet)
        ? h.snippet.filter(isRecord).map((p) => ({ text: str(p.text) ?? '', match: p.match === true }))
        : [];
      hits.push({ recordId: str(h.recordId)!, name: str(h.name), snippet });
    }
    groups.push({
      object: str(g.object)!,
      label: str(g.label) ?? str(g.object)!,
      count: typeof g.count === 'number' ? g.count : hits.length,
      countCapped: g.countCapped === true,
      hits,
    });
  }
  return { kind: 'ok', groups };
}

async function askKernel(term: string): Promise<SearchAnswer> {
  try {
    const res = await searchFetch(`/api/search/global?q=${encodeURIComponent(term)}`);
    return readAnswer(await res.json());
  } catch {
    return { kind: 'error' };
  }
}

export interface GlobalSearchOptions {
  readonly destinations: ReadonlyMap<string, SearchDestination>;
  readonly navigate: (href: string) => void;
}

/** Build the search box and its results panel, slotted into the topbar's centre. */
export function createGlobalSearch(options: GlobalSearchOptions): HTMLElement {
  const root = document.createElement('div');
  root.className = 'caos-search';
  root.setAttribute('slot', 'center');

  const form = document.createElement('form');
  form.className = 'caos-search__form';
  form.setAttribute('role', 'search');

  const input = document.createElement('input');
  input.type = 'search';
  input.name = 'q';
  input.className = 'caos-search__input';
  input.placeholder = 'Search';
  input.setAttribute('aria-label', 'Search');
  input.autocomplete = 'off';
  form.append(input);

  const panel = document.createElement('div');
  panel.className = 'caos-search__results';
  panel.setAttribute('role', 'region');
  panel.setAttribute('aria-label', 'Search results');
  panel.setAttribute('aria-live', 'polite');
  panel.hidden = true;

  root.append(form, panel);

  const close = (): void => {
    panel.hidden = true;
    panel.replaceChildren();
  };

  const say = (text: string, className: string): void => {
    const p = document.createElement('p');
    p.className = className;
    p.textContent = text;
    panel.replaceChildren(p);
    panel.hidden = false;
  };

  // Only the answer to the LAST term asked is drawn: a slow earlier search finishing after a newer
  // one must not replace the newer results.
  let asked = 0;

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const term = input.value.trim();
    if (term === '') {
      close();
      return;
    }
    const mine = ++asked;
    say('Searching…', 'caos-search__status');
    void askKernel(term).then((answer) => {
      if (mine !== asked) return;
      render(term, answer);
    });
  });

  root.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') close();
  });

  function render(term: string, answer: SearchAnswer): void {
    if (answer.kind === 'error') {
      say('Search is unavailable right now. Try again in a moment.', 'caos-search__status caos-search__status--error');
      return;
    }
    if (answer.kind === 'narrow') {
      say(answer.guidance || 'Too many searches are running right now. Please retry in a moment.', 'caos-search__status');
      return;
    }
    if (answer.groups.length === 0) {
      say(`No results for “${term}”.`, 'caos-search__status');
      return;
    }
    panel.replaceChildren(...answer.groups.map(renderGroup));
    panel.hidden = false;
  }

  function renderGroup(group: SearchGroup): HTMLElement {
    const section = document.createElement('section');
    section.className = 'caos-search__group';
    section.dataset.object = group.object;

    const heading = document.createElement('h3');
    heading.className = 'caos-search__group-heading';
    const label = document.createElement('span');
    label.textContent = group.label;
    const count = document.createElement('span');
    count.className = 'caos-search__count';
    count.textContent = group.countCapped ? `${group.count}+` : String(group.count);
    heading.append(label, count);

    const list = document.createElement('ul');
    list.className = 'caos-search__hits';
    const destination = options.destinations.get(group.object);
    for (const hit of group.hits) list.append(renderHit(hit, destination));

    section.append(heading, list);
    return section;
  }

  function renderHit(hit: SearchHit, destination: SearchDestination | undefined): HTMLElement {
    const item = document.createElement('li');
    item.className = 'caos-search__hit';
    item.dataset.recordId = hit.recordId;

    const title = hit.name ?? (hit.snippet.map((p) => p.text).join('').trim() || hit.recordId);
    if (destination) {
      const link = document.createElement('a');
      link.className = 'caos-search__hit-link';
      link.href = `/${destination.app}/${destination.tab}/${hit.recordId}`;
      link.textContent = title;
      link.addEventListener('click', (ev) => {
        ev.preventDefault();
        close();
        options.navigate(link.getAttribute('href')!);
      });
      item.append(link);
    } else {
      const name = document.createElement('span');
      name.className = 'caos-search__hit-name';
      name.textContent = title;
      item.append(name);
    }

    if (hit.snippet.length > 0) {
      const snippet = document.createElement('p');
      snippet.className = 'caos-search__snippet';
      for (const part of hit.snippet) {
        if (part.match) {
          const mark = document.createElement('mark');
          mark.textContent = part.text;
          snippet.append(mark);
        } else {
          snippet.append(document.createTextNode(part.text));
        }
      }
      item.append(snippet);
    }
    return item;
  }

  return root;
}
